import React, { useState } from 'react';
import { xaiExamples } from '../data/xai_examples';
import ExecutiveAnchor from '../components/xai/dashboard/ExecutiveAnchor';
import ModalityWeights from '../components/xai/dashboard/ModalityWeights';
import WeightSimulator from '../components/xai/dashboard/WeightSimulator';
import ActionPanel from '../components/xai/dashboard/ActionPanel';

export default function LiveDiagnosticCaseReview() {
  const [activePlatform, setActivePlatform] = useState('acoustic');
  
  const currentPlatformData = xaiExamples[activePlatform];
  const activeCase = currentPlatformData?.examples[0];

  // Restructure the 12 paths into logical menu sub-groups
  const navigationGroups = {
    "Single-Modality Edge": [
      { id: 'acoustic', title: 'Acoustic AI', icon: '🎙️' },
      { id: 'radiology', title: 'Radiology AI', icon: '🩻' },
      { id: 'pathology', title: 'Pathology AI', icon: '🔬' }
    ],
    "Context Enrichment": [
      { id: 'clinical_lab', title: 'Clinical Labs', icon: '📊' },
      { id: 'acoustic_clinical', title: 'Acoustic + Clinical', icon: '🎙️📊' },
      { id: 'radiology_clinical', title: 'Radiology + Clinical', icon: '🩻📊' }
    ],
    "Multi-Modal Fusion Core": [
      { id: 'fusion_rad_path', title: 'Radiology + Pathology', icon: '🧬' },
      { id: 'fusion_rad_acoustic', title: 'Radiology + Acoustic', icon: '🎧' },
      { id: 'fusion_path_acoustic', title: 'Pathology + Acoustic', icon: '🎤' },
      { id: 'fusion_rad_acoustic_clinical', title: 'Triple Fusion Core', icon: '🩻🎙️📊' },
      { id: 'fusion_all_except_omics', title: 'Quad Fusion Engine', icon: '🌐🫁' },
      { id: 'fusion_molecular_omic', title: 'Molecular Omic Fusion', icon: '🔬🧬📊' }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Control Panel: Sidebar Navigation for the 12 Modules */}
        <div className="lg:col-span-3 space-y-6 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">XAI Core Engine</h2>
            <p className="text-xs text-slate-400 mt-0.5">Toggle platform permutations</p>
          </div>
          
          {Object.entries(navigationGroups).map(([groupName, platforms]) => (
            <div key={groupName} className="space-y-1">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 mb-1">
                {groupName}
              </h3>
              {platforms.map((p) => {
                const isActive = activePlatform === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setActivePlatform(p.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg transition-all text-left border ${
                      isActive 
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 font-semibold shadow-sm' 
                        : 'text-slate-600 border-transparent hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <span className="text-sm">{p.icon}</span>
                    <span className="truncate">{p.title}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Right Execution Engine Display */}
        <div className="lg:col-span-9 space-y-6">
          {activeCase ? (
            <>
              {/* Layer 3 and Verdict Anchor */}
              <ExecutiveAnchor prediction={activeCase.prediction} patientId="DEMO-001" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Layer 1 Analytics */}
                <ModalityWeights evidenceLayers={activeCase.evidence_layers} />
                
                {/* Live Sandbox Adjuster */}
                <WeightSimulator 
                  platformId={activePlatform} 
                  examples={currentPlatformData.examples} 
                />
              </div>

              {/* Layer 2 and Recommended Interventions Closure */}
              <ActionPanel 
                actions={activeCase.recommended_actions || []} 
                explanation={activeCase.clinical_explanation || ''} 
              />
            </>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 italic text-sm">
              Select a diagnostic configuration matrix from the control plane to verify active multi-modal inferences.
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
